import { ClassCell } from "@components/spreadsheet/cells/ClassCell";
import { CounterpartyCell } from "@components/spreadsheet/cells/CounterpartyCell";
import { EntityCell } from "@components/spreadsheet/cells/EntityCell";
import { FinancialAccountCell } from "@components/spreadsheet/cells/FinancialAccountCell";
import { RowNumberCell } from "@components/spreadsheet/cells/RowNumberCell";
import {
    ColumnPinInfo,
    PINNED_CELL_Z_INDEX,
} from "@components/spreadsheet/types";
import { SpreadsheetGridCommonProps } from "@components/spreadsheet/SpreadsheetGridRow";
import { isFunction } from "@common/helpers/typeGuards";
import classNames from "classnames";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import {
    AccountClassCell,
    AccountGroupCell,
    AccountSearchCell,
    AccountSubtypeCell,
    AccountTypeCell,
    BooleanCell,
    CategoryCell,
    CellType,
    DateCell,
    MathCell,
    NumberCell,
    StringCell,
} from "./cells";
import styles from "./SpreadsheetGrid.module.scss";
import { CellValue, ColumnDef } from "./types";
import type { SpreadsheetRangeAction } from "./rangeAction";
import { SpreadsheetRangeActionButton } from "./SpreadsheetRangeActionButton";
import { SpreadsheetTreeCellContent } from "./SpreadsheetTreeCellContent";

interface Props extends Omit<
    SpreadsheetGridCommonProps,
    "selectedCell" | "editingCell"
> {
    colIdx: number;
    column: ColumnDef;
    isFirstCol: boolean;
    isLastCol: boolean;
    isSelected: boolean;
    isEditing: boolean;
    pinInfo?: ColumnPinInfo;
    nestingDepth?: number;
    nestingIndentPx?: number;
    hasChildren?: boolean;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    /** Leads the tree column with the report look's chevron, or its spacer on leaves. */
    hasTreeLead?: boolean;
    /** Set only on the cell that shows the range action button. */
    rangeAction?: SpreadsheetRangeAction;
}

const SpreadsheetGridCellBase: React.FC<Props> = ({
    isSelected,
    rowIdx,
    colIdx,
    selectionRange,
    isEditing: isEditingProp,
    column,
    row,
    editedCellValue,
    exitEditMode,
    setEditedCellValue,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleFillHandleMouseDown,
    fillMode,
    isFirstRow,
    isFirstCol,
    isLastRow,
    isLastCol,
    disableDragFill,
    enterEditMode,
    registerCellRef,
    handleAddRow,
    handleRemoveRow,
    enterEditModeKey,
    showDropdownIndicator,
    isEmptyRow,
    isCellEditable,
    pinInfo,
    nestingDepth = 0,
    nestingIndentPx = 16,
    hasChildren: _hasChildren = false,
    isExpanded = false,
    onToggleExpand,
    hasTreeLead = false,
    rangeAction,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditingProp && inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            if ((inputRef.current as HTMLInputElement).type === "text") {
                inputRef.current.setSelectionRange(length, length);
            }
        }
    }, [isEditingProp]);

    // Determine if this cell is the bottom-right of the selection range
    let showFillHandle: boolean;
    const rangeActive =
        selectionRange.start !== null &&
        selectionRange.end !== null &&
        (selectionRange.start.row !== selectionRange.end.row ||
            selectionRange.start.col !== selectionRange.end.col);
    if (rangeActive && selectionRange.start && selectionRange.end) {
        const rowMax = Math.max(
            selectionRange.start.row,
            selectionRange.end.row,
        );
        const colMax = Math.max(
            selectionRange.start.col,
            selectionRange.end.col,
        );
        showFillHandle = Boolean(
            rowIdx === rowMax && colIdx === colMax && !isEditingProp,
        );
    } else {
        showFillHandle = Boolean(isSelected && !isEditingProp);
    }

    // Guard against undefined row (can happen during paste to sparse array)
    const safeRow = useMemo(() => row ?? {}, [row]);

    const isValid =
        column.isValid?.({
            row: rowIdx,
            col: colIdx,
            value: safeRow[column.field],
            rowData: safeRow,
        }) ?? true;
    const isEditable =
        column.isEditable !== false &&
        (!isFunction(column.isEditable) ||
            column.isEditable({ row: rowIdx, col: colIdx })) &&
        !isEmptyRow &&
        isCellEditable?.(rowIdx, colIdx) !== false;

    // Render cell content based on cell type
    const renderCellContent = useCallback(() => {
        // Don't render anything for empty spare rows
        if (isEmptyRow) {
            return null;
        }

        const isEditing = isEditingProp;
        const value = isEditing ? editedCellValue : safeRow[column.field];

        if (
            (value === undefined || value === null) &&
            column.placeholder &&
            !isEditing
        ) {
            return (
                <span className={styles.cellPlaceholder}>
                    {column.placeholder}
                </span>
            );
        }

        const commonProps = {
            isEditing,
            onBlur: () => exitEditMode(),
            inputRef: isEditing ? inputRef : undefined,
            finishEditing: (val?: CellValue) =>
                exitEditMode(
                    val !== undefined
                        ? { value: val, rowIdx, colIdx }
                        : undefined,
                ),
            rowIdx,
            colId: column.field,
            onChange: setEditedCellValue,
            removeRow: handleRemoveRow,
            addRow: handleAddRow,
            row: safeRow,
            enterEditModeKey,
            showDropdownIndicator,
        };

        if (column.cellFn) {
            const customRendered = column.cellFn({ ...commonProps, value });
            if (customRendered !== undefined) {
                return customRendered;
            }
        }

        switch (column.cellType) {
            case CellType.ROW_NUMBER:
                return <RowNumberCell {...commonProps} value={value} />;
            case CellType.NUMBER:
                return (
                    <NumberCell
                        {...commonProps}
                        value={value as number | null}
                        isMonetary={column.isMonetary}
                        allowNegative={column.allowNegative}
                    />
                );
            case CellType.MATH:
                return (
                    <MathCell
                        {...commonProps}
                        value={value as number | null}
                        isMonetary={column.isMonetary}
                    />
                );
            case CellType.DATE:
                return (
                    <DateCell {...commonProps} value={value as string | null} />
                );
            case CellType.STRING:
                return (
                    <StringCell
                        {...commonProps}
                        value={value as string | null}
                    />
                );
            case CellType.CATEGORY:
                return (
                    <CategoryCell
                        {...commonProps}
                        value={value as string | null}
                    />
                );
            case CellType.ACCOUNT_TYPE:
                return (
                    <AccountTypeCell
                        {...commonProps}
                        value={value as string | null}
                        availableClassifications={
                            column.availableClassifications
                        }
                    />
                );
            case CellType.ACCOUNT_GROUP:
                return (
                    <AccountGroupCell
                        {...commonProps}
                        value={typeof value === "string" ? value : null}
                        groups={column.groups ?? []}
                    />
                );
            case CellType.ACCOUNT_SUBTYPE:
                return (
                    <AccountSubtypeCell
                        {...commonProps}
                        availableClassifications={
                            column.availableClassifications
                        }
                        legalType={column.legalType}
                        value={value as string | null}
                    />
                );
            case CellType.ACCOUNT_SEARCH:
                return (
                    <AccountSearchCell
                        {...commonProps}
                        value={value as string | null}
                        accounts={column.accounts ?? []}
                        cta={column.cta}
                        newAccountInfo={column.newAccountInfo}
                    />
                );
            case CellType.ACCOUNT_CLASS:
                return (
                    <AccountClassCell
                        {...commonProps}
                        value={value as string | null}
                        balanceSheetClassesOnly={column.balanceSheetClassesOnly}
                    />
                );
            case CellType.CLASS:
                return (
                    <ClassCell
                        {...commonProps}
                        value={value as string[] | null}
                    />
                );
            case CellType.COUNTERPARTY:
                return (
                    <CounterpartyCell
                        {...commonProps}
                        value={value as string | null}
                        counterpartiesMap={column.counterpartiesMap}
                    />
                );
            case CellType.ENTITY:
                return (
                    <EntityCell
                        {...commonProps}
                        value={value as number | null}
                        entities={column.entities}
                    />
                );
            case CellType.FINANCIAL_ACCOUNT:
                return (
                    <FinancialAccountCell
                        {...commonProps}
                        value={typeof value === "number" ? value : null}
                    />
                );
            case CellType.BOOLEAN:
                return (
                    <BooleanCell
                        {...commonProps}
                        value={typeof value === "boolean" ? value : null}
                        disabled={!isEditable}
                    />
                );
            case CellType.LEDGER:
                return null;
            default:
                throw new Error(`Unknown cell type: ${column.cellType}`);
        }
    }, [
        isEditingProp,
        exitEditMode,
        editedCellValue,
        column,
        safeRow,
        setEditedCellValue,
        colIdx,
        rowIdx,
        handleAddRow,
        handleRemoveRow,
        enterEditModeKey,
        isEmptyRow,
        isEditable,
        showDropdownIndicator,
    ]);

    // Helper to check if a cell is in the selection rectangle
    const isCellInSelection = useCallback(() => {
        if (!selectionRange.start || !selectionRange.end) {
            return false;
        }
        const rowMin = Math.min(
            selectionRange.start.row,
            selectionRange.end.row,
        );
        const rowMax = Math.max(
            selectionRange.start.row,
            selectionRange.end.row,
        );
        const colMin = Math.min(
            selectionRange.start.col,
            selectionRange.end.col,
        );
        const colMax = Math.max(
            selectionRange.start.col,
            selectionRange.end.col,
        );
        return (
            rowIdx >= rowMin &&
            rowIdx <= rowMax &&
            colIdx >= colMin &&
            colIdx <= colMax
        );
    }, [selectionRange, rowIdx, colIdx]);

    // Helpers for edge borders
    const isTopEdge = useCallback(() => {
        if (!selectionRange.start || !selectionRange.end) {
            return false;
        }
        const rowMin = Math.min(
            selectionRange.start.row,
            selectionRange.end.row,
        );
        return isCellInSelection() && rowIdx === rowMin;
    }, [selectionRange, isCellInSelection, rowIdx]);

    const isBottomEdge = useCallback(() => {
        if (!selectionRange.start || !selectionRange.end) {
            return false;
        }
        const rowMax = Math.max(
            selectionRange.start.row,
            selectionRange.end.row,
        );
        return isCellInSelection() && rowIdx === rowMax;
    }, [selectionRange, isCellInSelection, rowIdx]);

    const isLeftEdge = useCallback(() => {
        if (!selectionRange.start || !selectionRange.end) {
            return false;
        }
        const colMin = Math.min(
            selectionRange.start.col,
            selectionRange.end.col,
        );
        return isCellInSelection() && colIdx === colMin;
    }, [selectionRange, isCellInSelection, colIdx]);

    const isRightEdge = useCallback(() => {
        if (!selectionRange.start || !selectionRange.end) {
            return false;
        }
        const colMax = Math.max(
            selectionRange.start.col,
            selectionRange.end.col,
        );
        return isCellInSelection() && colIdx === colMax;
    }, [selectionRange, isCellInSelection, colIdx]);

    const isInFillArea = useMemo(() => {
        if (!fillMode.isFilling || !fillMode.start || !fillMode.end) {
            return false;
        }

        let srcStartRow = fillMode.start.row;
        let srcEndRow = fillMode.start.row;
        let srcStartCol = fillMode.start.col;
        let srcEndCol = fillMode.start.col;
        if (
            selectionRange.start &&
            selectionRange.end &&
            (selectionRange.start.row !== selectionRange.end.row ||
                selectionRange.start.col !== selectionRange.end.col)
        ) {
            srcStartRow = Math.min(
                selectionRange.start.row,
                selectionRange.end.row,
            );
            srcEndRow = Math.max(
                selectionRange.start.row,
                selectionRange.end.row,
            );
            srcStartCol = Math.min(
                selectionRange.start.col,
                selectionRange.end.col,
            );
            srcEndCol = Math.max(
                selectionRange.start.col,
                selectionRange.end.col,
            );
        }

        // Target fill area is from the source range to the fill endpoint
        const tgtStartRow = Math.min(srcStartRow, fillMode.end.row);
        const tgtEndRow = Math.max(srcEndRow, fillMode.end.row);
        const tgtStartCol = Math.min(srcStartCol, fillMode.end.col);
        const tgtEndCol = Math.max(srcEndCol, fillMode.end.col);

        // Highlight all cells in the fill area except the source range
        const inFillArea =
            rowIdx >= tgtStartRow &&
            rowIdx <= tgtEndRow &&
            colIdx >= tgtStartCol &&
            colIdx <= tgtEndCol;
        const inSource =
            rowIdx >= srcStartRow &&
            rowIdx <= srcEndRow &&
            colIdx >= srcStartCol &&
            colIdx <= srcEndCol;

        return inFillArea && !inSource;
    }, [fillMode, selectionRange, rowIdx, colIdx]);

    const cellClassName = useMemo(() => {
        const classes = [styles.cell];

        if (isFirstCol) {
            classes.push(styles.firstColCell);
        }
        if (isFirstRow) {
            classes.push(styles.firstRowCell);
        }
        if (isLastCol) {
            classes.push(styles.lastColCell);
        }
        if (isLastRow) {
            classes.push(styles.lastRowCell);
        }

        if (isCellInSelection()) {
            classes.push(styles.selected);
            if (isTopEdge()) {
                classes.push(styles.selectedTop);
            }
            if (isBottomEdge()) {
                classes.push(styles.selectedBottom);
            }
            if (isLeftEdge()) {
                classes.push(styles.selectedLeft);
            }
            if (isRightEdge()) {
                classes.push(styles.selectedRight);
            }
        }

        if (isSelected && !rangeActive) {
            classes.push(styles.focusedCell);
        }

        if (isInFillArea) {
            classes.push(styles.fillPreview);
        }

        if (!isValid) {
            classes.push(styles.invalidCell);
        }

        if (
            column.cellType === CellType.NUMBER ||
            column.cellType === CellType.MATH
        ) {
            classes.push(styles.numberCell);
        }

        if (!isEditable) {
            classes.push(styles.notEditableCell);
        }

        if (column.className) {
            if (typeof column.className === "function") {
                classes.push(column.className(safeRow));
            } else {
                classes.push(column.className);
            }
        }

        if (pinInfo?.isPinnedLeft) {
            classes.push(styles.pinnedLeftCell);
        }
        if (pinInfo?.isPinnedRight) {
            classes.push(styles.pinnedRightCell);
        }
        if (pinInfo?.isLastPinnedLeft) {
            classes.push(styles.lastPinnedLeft);
        }
        if (pinInfo?.isFirstPinnedRight) {
            classes.push(styles.firstPinnedRight);
        }

        return classNames(classes);
    }, [
        isCellInSelection,
        isTopEdge,
        isBottomEdge,
        isLeftEdge,
        isRightEdge,
        isSelected,
        column,
        isValid,
        isFirstCol,
        isFirstRow,
        isLastCol,
        isLastRow,
        rangeActive,
        isInFillArea,
        isEditable,
        safeRow,
        pinInfo,
    ]);

    const cellStyle = useMemo(() => {
        if (
            !pinInfo?.pinnedStyle ||
            Object.keys(pinInfo.pinnedStyle).length === 0
        ) {
            return undefined;
        }
        // Bump z-index when focused so the selection border overlaps adjacent cells
        return isSelected
            ? {
                  ...pinInfo.pinnedStyle,
                  zIndex: PINNED_CELL_Z_INDEX + 1,
              }
            : pinInfo.pinnedStyle;
    }, [pinInfo, isSelected]);

    const hasNestingLead =
        !hasTreeLead && (onToggleExpand !== undefined || nestingDepth > 0);

    return (
        <div
            key={column.field + rowIdx}
            className={cellClassName}
            style={cellStyle}
            tabIndex={-1}
            ref={(el) => registerCellRef(rowIdx, colIdx, el)}
            onMouseDown={(e) => {
                handleCellMouseDown(e, rowIdx, colIdx);
            }}
            onMouseEnter={() => handleCellMouseEnter(rowIdx, colIdx)}
            {...(column.cellType === CellType.ROW_NUMBER
                ? {
                      onContextMenu: (e) => {
                          e.preventDefault();
                          enterEditMode(rowIdx, colIdx, e);
                      },
                  }
                : {
                      onDoubleClick: (e) => {
                          enterEditMode(rowIdx, colIdx, e);
                      },
                      ...(column.isEditOnSingleClick
                          ? {
                                onClick: (e: React.MouseEvent) => {
                                    if (!isEditingProp) {
                                        enterEditMode(rowIdx, colIdx, e);
                                    }
                                },
                            }
                          : {}),
                  })}
            role="gridcell"
        >
            {hasTreeLead && (
                <SpreadsheetTreeCellContent
                    depth={nestingDepth}
                    indentPx={nestingIndentPx}
                    isExpanded={isExpanded}
                    onToggleExpand={onToggleExpand}
                >
                    {renderCellContent()}
                </SpreadsheetTreeCellContent>
            )}
            {hasNestingLead && (
                <div
                    className={styles.cellContent}
                    style={
                        nestingDepth > 0
                            ? { paddingLeft: nestingDepth * nestingIndentPx }
                            : undefined
                    }
                >
                    {onToggleExpand && (
                        <button
                            className={classNames(styles.expandToggle, {
                                [styles.expandToggleOpen]: isExpanded,
                            })}
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggleExpand();
                            }}
                            tabIndex={-1}
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            type="button"
                        />
                    )}
                    {renderCellContent()}
                </div>
            )}
            {!hasTreeLead && !hasNestingLead && (
                <div className={styles.cellContent}>{renderCellContent()}</div>
            )}
            {showFillHandle && !disableDragFill && (
                <div
                    className={styles.fillHandle}
                    onMouseDown={(e) =>
                        handleFillHandleMouseDown(e, rowIdx, colIdx)
                    }
                    onClick={(e) => e.stopPropagation()}
                    role="button"
                    aria-label="Fill handle"
                    tabIndex={0}
                />
            )}
            {rangeAction && (
                <SpreadsheetRangeActionButton action={rangeAction} />
            )}
        </div>
    );
};

export const SpreadsheetGridCell = memo(SpreadsheetGridCellBase);
